// ============================================
// Error Boundary Component
// ============================================

import { Component, type ComponentChildren } from 'preact';
import type { AppError, ErrorType } from '@/types';

interface Props {
  children: ComponentChildren;
  fallback?: (error: AppError, reset: () => void) => ComponentChildren;
}

interface State {
  error: AppError | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return {
      error: {
        type: 'INITIALIZATION_FAILED' as ErrorType,
        message: error.message || 'An unexpected error occurred',
        recoverable: true
      }
    };
  }

  componentDidCatch(error: Error, errorInfo: { componentStack?: string }) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    const { children, fallback } = this.props;

    if (error) {
      if (fallback) {
        return fallback(error, this.reset);
      }

      return (
        <div className="flex flex-col items-center justify-center min-h-screen p-8 bg-gray-50">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
            <div className="text-5xl mb-4">😵</div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">
              Something went wrong
            </h2>
            <p className="text-gray-600 mb-4">
              {error.message}
            </p>
            {error.recoverable && (
              <button
                onClick={this.reset}
                className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
              >
                Try Again
              </button>
            )}
          </div>
        </div>
      );
    }

    return children;
  }
}
